import { Inject, Injectable, OnDestroy } from '@angular/core';
import { defaultTxt, simpleValueValidation } from '@miq/utils';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { InterpolationFillerService } from './interpolation-filler.service';
import { BlueprintBase, ColumnBase, GroupBase, InterpolatedBlueprint, Layout } from './models/blueprint.model';
import { DataObject, ReportInfo, ReportOptions, ReportRecord } from './models/report.model';
import {
  DisplayFnArgumentsInfo,
  LayoutAndRecords,
  MappedTemplateField,
  PropertyGroup,
  TemplateField,
  TemplateFieldsCatalog,
} from './models/template.model';
import { ReportExportViewService } from './report-export-view.service';
import { ReportFieldFormatterService } from './report-field-formatter.service';
import { PropertyGroupService } from './report-group.service';
import { AVOID_API_FOR_COLUMNS } from './shared/constants/uris.const';
import { disclaimerFont, disclaimerLang, LocaleKeys } from './shared/templates/utils/template-display-sort-order.const';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { TEMPLATE_CONFIGURATIONS } from './shared/templates/template-display-fn.const';

const disclaimerLabel = `disclaimer`;
@Injectable()
export class InterpolatedBlueprintService implements OnDestroy {
  private _interpolationsByTemplateId: {
    [templateId: number]: { [layout: string]: BehaviorSubject<InterpolatedBlueprint> };
  } = {};

  constructor(
    private reportFieldFormatterService: ReportFieldFormatterService,
    private reportExportViewService: ReportExportViewService,
    private interpolationFillerService: InterpolationFillerService,
    private propertyGroupService: PropertyGroupService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  ngOnDestroy(): void {
    Object.values(this._interpolationsByTemplateId).forEach(layout => {
      Object.values(layout).forEach(interpolatedBluePrint => interpolatedBluePrint.complete());
    });
  }

  isLayoutExist(templateId: number, layout: string): boolean {
    return (
      this._interpolationsByTemplateId[templateId] != null &&
      this._interpolationsByTemplateId[templateId][layout] != null
    );
  }

  fetchInterpolationByTemplateId(templateId: number, layout: Layout): InterpolatedBlueprint {
    return this.getInterpolationByTemplateId(templateId, layout).value;
  }

  fetchInterpolationByTemplateId$(templateId: number, layout: Layout): Observable<InterpolatedBlueprint> {
    return this.getInterpolationByTemplateId(templateId, layout).asObservable();
  }

  setInterpolationByTemplateId(templateId: number, interpolatedBlueprint: InterpolatedBlueprint, layout: Layout) {
    this.getInterpolationByTemplateId(templateId, layout).next(interpolatedBlueprint);
  }

  private getInterpolationByTemplateId(templateId: number, layout: Layout) {
    if (this._interpolationsByTemplateId[templateId] == null) {
      const _layout = {};
      _layout[layout] = new BehaviorSubject<InterpolatedBlueprint>(null);
      this._interpolationsByTemplateId[templateId] = _layout;
    } else if (this._interpolationsByTemplateId[templateId][layout] == null) {
      this._interpolationsByTemplateId[templateId][layout] = new BehaviorSubject<InterpolatedBlueprint>(null);
    }
    return this._interpolationsByTemplateId[templateId][layout];
  }

  getImages(reportId: number) {
    return this.reportExportViewService.getImages(reportId);
  }

  mapInterpolatedBlueprint(
    blueprint: BlueprintBase,
    templateCatalog: TemplateFieldsCatalog,
    entityCatalog: { [entity: string]: { [columnId: number]: any } },
    currentEntity: string,
    localeId?: LocaleKeys,
    templateId?: number
  ): InterpolatedBlueprint {
    const newTemplateCatalog = this.transformEntityCatalogIntoMap(entityCatalog, currentEntity, templateId);
    return Object.keys(blueprint).map(key => {
      const groups: Array<GroupBase<TemplateField>> = this.transformBlueprintColumnsIntoGroups(
        blueprint[key],
        templateCatalog,
        newTemplateCatalog,
        localeId,
        templateId
      );
      return groups;
    }, []);
  }

  private transformBlueprintColumnsIntoGroups(
    column: ColumnBase,
    templateCatalog: TemplateFieldsCatalog,
    newTemplateCatalog: { [p: number]: DisplayFnArgumentsInfo },
    localeId?: LocaleKeys,
    templateId?: number
  ): Array<GroupBase<TemplateField>> {
    return Object.values(column || ({} as ColumnBase)).map((group: GroupBase) => {
      const fields = this.interpolateGroupFieldsWithTemplateFieldsCatalog(
        group.fields,
        templateCatalog,
        newTemplateCatalog,
        localeId,
        templateId
      );
      return { ...group, fields };
    });
  }

  private interpolateGroupFieldsWithTemplateFieldsCatalog(
    fields: Array<{ id: number; [p: string]: any }>,
    templateCatalog: TemplateFieldsCatalog,
    newTemplateCatalog: { [p: number]: DisplayFnArgumentsInfo },
    localeId?: LocaleKeys,
    templateId?: number
  ) {
    /*
     * "fields" being null or undefined means that one of the groups of the
     * currently selected blueprint is missing the "fields" attribute
     */
    if (fields == null) {
      throw new Error(`Current Blueprint group is missing the fields attribute`);
    } else {
      return fields.map<TemplateField>(field => {
        const templateField: TemplateField = templateCatalog[field.id];
        if (field.label === disclaimerLabel) {
          field.value = disclaimerLang[localeId];
          field.options.fontFace = disclaimerFont[localeId];
        }

        // Priority order for displayFnArguments:
        // 1. From displayFnMap (TEMPLATE_CONFIGURATIONS) - frontend config
        // 2. From templateField (API template catalog) - backend response
        let displayFnArguments: DisplayFnArgumentsInfo[] = [];

        // Try to get displayFnArguments from displayFnMap first
        if (templateId != null) {
          const displayFnMap = TEMPLATE_CONFIGURATIONS[templateId]?.fieldsDisplayFunctions;
          const displayFnConfig = displayFnMap?.[field.id];
          if (displayFnConfig?.displayFnArguments && displayFnConfig.displayFnArguments.length > 0) {
            displayFnArguments = this.interpolateDisplayFnArgumentsWithEntityCatalogColumns(
              displayFnConfig.displayFnArguments,
              newTemplateCatalog,
              field.id,
              displayFnConfig // Pass the template config
            );
          }
        }

        // Fallback to API templateField if no displayFnMap arguments found
        if (displayFnArguments.length === 0 && templateField != null) {
          displayFnArguments = this.interpolateDisplayFnArgumentsWithEntityCatalogColumns(
            templateField.displayFnArguments,
            newTemplateCatalog,
            field.id
            // No template config for fallback case
          );
        }
        return {
          ...field,
          ...templateField,
          displayFnArguments,
        };
      });
    }
  }

  private interpolateDisplayFnArgumentsWithEntityCatalogColumns(
    displayFnArguments: DisplayFnArgumentsInfo[],
    newTemplateCatalog: { [p: number]: DisplayFnArgumentsInfo },
    fieldId: number,
    _templateConfig?: any // renamed to _templateConfig to avoid unused var lint error
  ): DisplayFnArgumentsInfo[] {
    return displayFnArguments.map(originalDisplayFnArgument => {
      const mappedDisplayFnArgument = newTemplateCatalog[originalDisplayFnArgument.id];
      const mappedDisplayFnUOMArgument =
        originalDisplayFnArgument.uomId != null ? newTemplateCatalog[originalDisplayFnArgument.uomId] : null;
      if (
        mappedDisplayFnArgument == null ||
        (originalDisplayFnArgument.uomId != null && mappedDisplayFnUOMArgument == null)
      ) {
        const warnMessage =
          mappedDisplayFnArgument == null
            ? `Argument ${originalDisplayFnArgument.id} not found in entity catalog for field ${fieldId}`
            : '';
        const uomMissing =
          mappedDisplayFnUOMArgument == null
            ? `Argument UOM ${originalDisplayFnArgument.uomId} not found in entity catalog for field ${fieldId}`
            : '';
        const logParts = [warnMessage, uomMissing].filter(Boolean);
        if (logParts.length) {
          this.logger.info(logParts.join(' - '));
        }
      }
      return { ...originalDisplayFnArgument, ...mappedDisplayFnArgument, uomArgument: mappedDisplayFnUOMArgument };
    });
  }

  /**
   * As per current implementation, entity catalog is getting input in an object
   * structure, using the path as attribute name. Interpolated Blueprint
   * requires the same structure, but using the column ID as attribute key
   * instead.
   *
   * @param entityCatalog
   * @param currentEntity
   * @param templateId
   */
  private transformEntityCatalogIntoMap(
    entityCatalog: { [entity: string]: { [field: string]: { [att: string]: any } } } = {},
    currentEntity: string,
    templateId?: number
  ): { [columnId: number]: DisplayFnArgumentsInfo } {
    const columnUpdatableMap = this.buildColumnUpdatableMap(templateId);
    const result = Object.entries(entityCatalog).reduce<{ [columnId: number]: DisplayFnArgumentsInfo }>(
      (acc, [entity, mappedEntityColumns]) => {
        const flatMappedColumnsById = (Object.values(mappedEntityColumns) as any[]).reduce<{
          [columnId: number]: DisplayFnArgumentsInfo;
        }>(
          // TECH DEBT (Ramakrishna): Column Info Shape/Signature enhancement.
          (acc, column) => {
            // Get isTableDataUpdatable from TEMPLATE_CONFIGURATIONS lookup
            const isTableDataUpdatable = columnUpdatableMap[column.columnId];

            return {
              ...acc,
              [column.columnId]: {
                id: column.columnId,
                field: column.field,
                path: column.path,
                dataType: column.dataType,
                dataFormat: column.options && column.options.format,
                isUpdatable:
                  (!column.field.includes('/') && // not updatable if field is inside an array
                    currentEntity === entity &&
                    !!(column.parent && column.parent.update) &&
                    column.condition == null) ||
                  isTableDataUpdatable == true,
                lovValues:
                  column.lookupCategory != null && column.id !== AVOID_API_FOR_COLUMNS
                    ? this.reportFieldFormatterService.getLovValues(column.lookupCategory, column.columnId)
                    : undefined,
              } as DisplayFnArgumentsInfo,
            };
          },
          {}
        );
        return { ...acc, ...flatMappedColumnsById };
      },
      {}
    );
    return result;
  }

  /**
   * Builds a lookup map of columnId -> isTableDataUpdatable from TEMPLATE_CONFIGURATIONS.
   * Maps all columns used by fields (via displayFnArguments) to their isTableDataUpdatable setting.
   *
   * @param templateId
   * @returns Map of columnId to isTableDataUpdatable boolean value
   */
  private buildColumnUpdatableMap(templateId?: number): { [columnId: number]: boolean } {
    const columnUpdatableMap: { [columnId: number]: boolean } = {};

    if (templateId == null) {
      return columnUpdatableMap;
    }

    const displayFnMap = TEMPLATE_CONFIGURATIONS[templateId]?.fieldsDisplayFunctions;
    if (!displayFnMap) {
      return columnUpdatableMap;
    }

    Object.values(displayFnMap).forEach((fieldConfig: any) => {
      if (fieldConfig.displayFnArguments && fieldConfig.isTableDataUpdatable != null) {
        // Map all columns used by this field to its isTableDataUpdatable value
        fieldConfig.displayFnArguments.forEach((arg: any) => {
          if (arg.id) {
            columnUpdatableMap[arg.id] = fieldConfig.isTableDataUpdatable;
          }
        });
      }
    });

    return columnUpdatableMap;
  }

  flattenBlueprintColumnsToGroups(blueprint: InterpolatedBlueprint): Array<GroupBase<TemplateField>> {
    return [].concat(...blueprint);
  }

  fillInterpolationWithReportMainData(
    report: ReportInfo,
    stringInjectorMap?: { [key: string]: string | Function },
    layoutType?: Layout,
    templateId?: number
  ): InterpolatedBlueprint<MappedTemplateField> {
    let interpolatedDate: InterpolatedBlueprint;
    interpolatedDate = this.fetchInterpolationByTemplateId(templateId, layoutType);
    if (layoutType === Layout.Cover && interpolatedDate == null) {
      return null;
    }

    return interpolatedDate.map(column => {
      return column.map(group => {
        const fields = group.fields.map(field => {
          const { id, label, options, value: fieldValue, reportAttribute } = field;
          let reportValue: string | string[] = fieldValue;
          if (options && options.checkValue) {
            const result: boolean = report.records.every(record =>
              this.reportFieldFormatterService.isFieldVisible(record, { templateId, field })
            );
            reportValue = result ? reportValue : '';
          }

          if (fieldValue === 'PREPARED FOR: ' || fieldValue == null) {
            const attributeValue = decodeURIComponent(report[reportAttribute] as string);
            reportValue =
              (fieldValue === undefined ? '' : reportValue + ' ') +
              `${simpleValueValidation(attributeValue) ? attributeValue.toString().toUpperCase() : defaultTxt}`;
          }

          if (stringInjectorMap != null && !Array.isArray(reportValue)) {
            reportValue = reportValue.replace(/\${([^}]+)}/g, (_, prop) => {
              const injector = stringInjectorMap[prop];
              let value = injector.toString();
              if (typeof injector === 'function') {
                value = injector();
              }
              return value;
            });
          }

          return options != null
            ? { id, label, value: of(reportValue), options }
            : { id, label, value: of(reportValue) };
        });
        return { ...(group as GroupBase<TemplateField>), fields };
      });
    });
  }

  fillInterpolationWithReportData(
    records: ReportRecord[],
    templateId: number,
    settings?: Partial<{ joinString: boolean; onlyUrl: boolean; symbols: DataObject; reportOptions?: ReportOptions }>,
    stringInjectorMap?: { [key: string]: string | Function },
    reportId?: number
  ): InterpolatedBlueprint<MappedTemplateField>[] {
    const recordsInterpolations = records.map(record => {
      const layout = record.layout || Layout.MainLayout;
      const interpolationByTemplateId = this.fetchInterpolationByTemplateId(templateId, layout);
      const interpolationWithData = interpolationByTemplateId.map(column => {
        const pptFieldsOnly = column.filter(field => !field.filterOutInPPT);
        return pptFieldsOnly.map(group => {
          return this.interpolationFillerService.fillInterpolation(
            group,
            record,
            settings,
            templateId,
            stringInjectorMap,
            reportId
          );
        });
      });
      return interpolationWithData;
    });
    return recordsInterpolations;
  }

  /**
   * @todo this service is mainly used by the ppt-generator.service (MIQ) and
   *   we might need to review if we need to refactor it.
   *
   * @param templateId
   * @param mapAndRecords
   * @param reportId
   * @param settings
   * @param stringInjectorMap
   */
  fillInterpolationWithTemplateId(
    templateId: number,
    mapAndRecords: LayoutAndRecords[],
    reportId: number,
    settings?: Partial<{
      reportOptions: ReportOptions;
      joinString: boolean;
      onlyUrl: boolean;
      symbols: DataObject;
      recordsLength?: number;
    }>,
    stringInjectorMap?: { [key: string]: string | Function }
  ): InterpolatedBlueprint<MappedTemplateField>[] {
    const recordsInterpolations = mapAndRecords.map(itemForSlides => {
      // first layout always main layout, gets summary map, all remaining are split record layouts
      let restrictMapCallToOne = false;
      return this.fetchInterpolationByTemplateId(templateId, itemForSlides.layout).map(groups => {
        const groupData = [];
        // add only property details from second loop
        itemForSlides.splitArray.forEach(record => {
          groups
            .filter(field => !field.filterOutInPPT)
            .forEach(group => {
              // avoid adding map call from second time
              if (restrictMapCallToOne && group.isSummaryMapImage) {
                return;
              }
              if (group.isSummaryMapImage) {
                restrictMapCallToOne = true;
              }
              const interpolateDataFromHelper = this.interpolationFillerService.fillInterpolation(
                group,
                record,
                settings,
                templateId,
                stringInjectorMap,
                reportId
              );
              // collecting all interpolated data and returning all of them.
              groupData.push(interpolateDataFromHelper);
            });
        });
        return groupData;
      });
    });
    return recordsInterpolations;
  }

  setReportPropertyGroups(res: PropertyGroup[]) {
    this.propertyGroupService.setReportPropertyGroups(res);
  }

  getReportPropertyGroups() {
    return this.propertyGroupService.getReportPropertyGroups();
  }

  getReportPropertyGroupsStream() {
    return this.propertyGroupService.getReportPropertyGroupsStream();
  }
}
